import { useLocation, useNavigate } from '@tanstack/react-router'
import { type PropsWithChildren, useEffect, useRef, useState } from 'react'
import BlankLayout from '@/components/Layouts/BlankLayout'
import { LoadingSpinner } from '@/components/Loading/LoadingSpinner'
import { Logger } from '@/components/Logger'
import { usePriceChangedSubscription } from '@/hooks/subscriptions/useAssetPricesSubscription'
import { useAssetsSubscription } from '@/hooks/subscriptions/useAssetsSubscription'
import { useLeverageAccruedInterestSubscription } from '@/hooks/subscriptions/useLeverageAccruedInterestSubscription'
import { useUserBalanceSubscription } from '@/hooks/subscriptions/useUserBalanceSubscription'
import { useUserCouponPaymentsSubscription } from '@/hooks/subscriptions/useUserCouponPaymentsSubscription'
import { useUserPnlSubscription } from '@/hooks/subscriptions/useUserPnlSubscription'
import { useListOpenOrderBooks } from '@/hooks/useListOpenOrderBooks'
import useSignOutAndClearStore from '@/hooks/useSignOutAndClearStore'
import { useGetUserSelf, useListAssets } from '@/hooks/v1'
import { useApiKey } from '@/utils/apiKeyAuth'
import { useFirebaseAuth } from '@/utils/firebaseAuthProvider'
import { isPromoEntryPath, protectPromoEntry } from '@/utils/promoEntry'
import { setCurrentUserScope } from '@/utils/userScopedStorage'

const INVALID_BACKEND_AUTH_STATUSES = new Set([401, 403, 404])

const getErrorStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined
  }

  const directStatus = (error as { status?: unknown }).status
  if (typeof directStatus === 'number') {
    return directStatus
  }

  const responseStatus = (error as { response?: { status?: unknown } }).response?.status
  if (typeof responseStatus === 'number') {
    return responseStatus
  }

  return undefined
}

function useSubscriptionGate(delayMs: number) {
  const [ready, setReady] = useState(delayMs <= 0)

  useEffect(() => {
    if (delayMs <= 0) {
      return
    }

    const timer = setTimeout(() => setReady(true), delayMs)
    return () => clearTimeout(timer)
  }, [delayMs])

  return ready
}

function ProtectedContent({ children }: PropsWithChildren) {
  // Only mounted for protected routes; safe to run hooks here
  const subscriptionsReady = useSubscriptionGate(2500)
  useUserBalanceSubscription({ enabled: subscriptionsReady })
  usePriceChangedSubscription({ enabled: subscriptionsReady })
  useUserPnlSubscription({ enabled: subscriptionsReady })
  useListAssets()
  useListOpenOrderBooks()
  useAssetsSubscription({ enabled: subscriptionsReady })
  useLeverageAccruedInterestSubscription({ enabled: subscriptionsReady })
  useUserCouponPaymentsSubscription({ enabled: subscriptionsReady })
  return <>{children}</>
}

function AuthStatusPanel({ message }: { message: string }) {
  return (
    <div className="relative flex min-h-screen items-center justify-center bg-[url('')] bg-cover bg-center bg-no-repeat bg-[#060818] px-6 py-10 sm:px-16">
      <div className="relative flex w-full flex-col items-center justify-center gap-6 rounded-md bg-black/40 px-4 pb-16 pt-10 text-center backdrop-blur-lg sm:px-6 lg:max-w-[520px]">
        <h1 className="text-2xl font-extrabold uppercase text-white-dark md:text-3xl">Session</h1>
        <div className="flex flex-col items-center gap-4 text-center">
          <LoadingSpinner />
          <p className="text-base font-semibold text-white-dark">{message}</p>
        </div>
      </div>
    </div>
  )
}

// Public/auth pages must not mount cached identity checks or authenticated subscriptions.
export const ProtectedRoute = ({ children }: PropsWithChildren) => {
  const location = useLocation()
  const isPublicRoute =
    isPromoEntryPath(location.pathname) ||
    location.pathname.startsWith('/auth/') ||
    location.pathname === '/viewOnlyMode' ||
    location.pathname.startsWith('/proving-grounds') ||
    location.pathname === '/challenge' ||
    location.pathname.startsWith('/challenge/')
  if (isPublicRoute) {
    protectPromoEntry()
    return <BlankLayout />
  }
  return <AuthenticatedRoute>{children}</AuthenticatedRoute>
}

const AuthenticatedRoute = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate()
  const signOutAndClearStore = useSignOutAndClearStore()
  const user = useFirebaseAuth()
  const apiKey = useApiKey()
  const hasApiKeyAuth = Boolean(apiKey)
  const invalidBackendSessionHandled = useRef(false)

  const isSessionAuthenticated = Boolean(user) || hasApiKeyAuth
  const { data: userSelfResponse, error: userSelfError } = useGetUserSelf({
    query: {
      enabled: isSessionAuthenticated,
      retry: (failureCount, error) => {
        const status = getErrorStatus(error)
        if (status !== undefined && INVALID_BACKEND_AUTH_STATUSES.has(status)) {
          return false
        }
        return failureCount < 2
      },
    },
  })
  const backendUserId = userSelfResponse?.data?.id
  const backendErrorStatus = getErrorStatus(userSelfError)
  const hasInvalidBackendIdentityError =
    backendErrorStatus !== undefined && INVALID_BACKEND_AUTH_STATUSES.has(backendErrorStatus)
  const hasMissingBackendIdentity = Boolean(userSelfResponse) && !backendUserId
  const hasInvalidBackendIdentity =
    isSessionAuthenticated && (hasInvalidBackendIdentityError || hasMissingBackendIdentity)
  const isBackendIdentityCheckPending =
    isSessionAuthenticated && !userSelfResponse && !userSelfError

  useEffect(() => {
    if (backendUserId) {
      setCurrentUserScope(String(backendUserId))
      return
    }
    if (!isSessionAuthenticated) {
      setCurrentUserScope(null)
    }
  }, [backendUserId, isSessionAuthenticated])

  // If Firebase/API key auth is present but backend user is invalid, recover by clearing session
  useEffect(() => {
    if (!hasInvalidBackendIdentity) {
      invalidBackendSessionHandled.current = false
      return
    }

    if (invalidBackendSessionHandled.current) {
      return
    }
    invalidBackendSessionHandled.current = true

    signOutAndClearStore()
      .catch(() => {
        Logger.error('Unable to clear the expired session')
      })
      .finally(() => {
        navigate({ to: '/auth/sign-in', replace: true }).catch(() => {
          Logger.error('Unable to open sign-in after clearing the session')
        })
      })
  }, [
    backendErrorStatus,
    backendUserId,
    hasApiKeyAuth,
    hasInvalidBackendIdentity,
    hasInvalidBackendIdentityError,
    hasMissingBackendIdentity,
    navigate,
    signOutAndClearStore,
    user?.uid,
  ])

  // Navigation/redirect logic for protected routes
  useEffect(() => {
    if (user === null && !hasApiKeyAuth) {
      navigate({ to: '/auth/sign-in', replace: true }).catch(() => {
        Logger.error('Unable to open sign-in')
      })
    } else if (user && !user.emailVerified && !hasApiKeyAuth) {
      navigate({ to: '/auth/verify-email', replace: true }).catch(() => {
        Logger.error('Unable to open email verification')
      })
    }
  }, [user, navigate, hasApiKeyAuth])

  // Show loading while the user state is being determined (only for protected routes)
  if ((!user || user === undefined || user === null) && !hasApiKeyAuth) {
    return (
      <div className="flex h-screen">
        <div className="flex flex-col items-center justify-center m-auto">
          <LoadingSpinner className="" />
          <p className="mt-4 text-center">Loading user data...</p>
        </div>
      </div>
    )
  }

  if (hasInvalidBackendIdentity || isBackendIdentityCheckPending) {
    return (
      <AuthStatusPanel
        message={
          hasInvalidBackendIdentity ? 'Session expired. Redirecting...' : 'Validating session...'
        }
      />
    )
  }

  // If the user is authenticated and email is verified, render protected content which mounts hooks
  if (user?.emailVerified || hasApiKeyAuth) {
    return <ProtectedContent>{children}</ProtectedContent>
  }

  // For any other case, show loading (navigation should happen via useEffect)

  return <AuthStatusPanel message="Redirecting..." />
}
